import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import {
  CreditCard,
  Check,
  Rocket,
  Crown,
  Building2,
  ChevronDown,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import {
  CURRENCIES,
  RATES_UPDATED,
  formatCurrency,
  loadPreferredCurrency,
  savePreferredCurrency,
} from '../lib/currency';

const BASE_FEE_USD = 79.95;

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    min: 0,
    max: 500,
    rateUsd: 0.79,
    icon: Rocket,
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    desc: 'For small tutoring practices',
    rangeLabel: '0–500 lessons/month',
  },
  {
    id: 'pro',
    name: 'Pro',
    min: 501,
    max: 2500,
    rateUsd: 0.64,
    icon: Crown,
    color: 'text-[#7662ac]',
    bg: 'bg-[#7662ac]/10',
    border: 'border-[#7662ac]/30',
    desc: 'For growing companies',
    rangeLabel: '501–2,500 lessons/month',
  },
  {
    id: 'scale',
    name: 'Scale',
    min: 2501,
    max: Infinity,
    rateUsd: 0.49,
    icon: Building2,
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    desc: 'For high-volume operations',
    rangeLabel: '2,501+ lessons/month',
  },
];

const tierForLessonCount = (count) =>
  TIERS.find((t) => count >= t.min && count <= t.max) || TIERS[0];

const Billing = () => {
  const { organizationId, developerAccess } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [usage, setUsage] = useState({ sessionsThisMonth: 0, hoursThisMonth: 0 });
  // Negotiated pricing (org_billing_agreements, read-only for customers). When set it replaces the volume tiers.
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState(loadPreferredCurrency());
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [billing, setBilling] = useState({
    status: 'inactive',
    last4: null,
    brand: null,
    exp: null,
    hasCustomer: false,
  });
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [discountInput, setDiscountInput] = useState('');
  const [discountBusy, setDiscountBusy] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    if (!organizationId) return;
    loadBilling();
  }, [organizationId]);

  const loadBilling = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const [{ data: sessions }, { data: org }, { data: agreementRow }] = await Promise.all([
        supabase
          .from('sessions')
          .select('duration_minutes')
          .eq('organization_id', organizationId)
          .gte('lesson_timestamp', monthStart),
        supabase
          .from('organizations')
          .select(
            'stripe_customer_id, billing_status, default_payment_last4, default_payment_brand, default_payment_exp, trial_ends_at, discount_code, discount_percent'
          )
          .eq('id', organizationId)
          .maybeSingle(),
        supabase
          .from('org_billing_agreements')
          .select('plan_name, rate_usd_per_hour, base_fee_usd_per_month, terms, effective_from')
          .eq('organization_id', organizationId)
          .maybeSingle(),
      ]);
      setAgreement(agreementRow || null);

      const totalMin = (sessions || []).reduce((s, sess) => s + (sess.duration_minutes || 0), 0);
      setUsage({
        sessionsThisMonth: (sessions || []).length,
        hoursThisMonth: Math.round((totalMin / 60) * 10) / 10,
      });
      setBilling({
        status: org?.billing_status || 'inactive',
        last4: org?.default_payment_last4 || null,
        brand: org?.default_payment_brand || null,
        exp: org?.default_payment_exp || null,
        hasCustomer: Boolean(org?.stripe_customer_id),
        trialEndsAt: org?.trial_ends_at ? new Date(org.trial_ends_at) : null,
        discountCode: org?.discount_code || null,
        discountPercent: org?.discount_percent != null ? Number(org.discount_percent) : null,
      });
    } catch (err) {
      console.error('Error loading billing:', err);
    }
    setLoading(false);
  };

  // Detect ?checkout=success after Stripe redirect and re-pull billing fields
  // (the webhook usually beats this race but we re-fetch anyway, then strip
  // the query so a refresh doesn't keep retriggering).
  useEffect(() => {
    if (!organizationId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      loadBilling();
      params.delete('checkout');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : '')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const handleAddPaymentMethod = async () => {
    if (!organizationId) return;
    setPaymentBusy(true);
    setPaymentError(null);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: { organization_id: organizationId },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (err) {
      console.error('stripe-create-checkout failed:', err);
      // supabase-js reports any non-2xx as a generic FunctionsHttpError — never show that raw.
      setPaymentError('Card payments are temporarily unavailable. Please contact support@tutortracer.com and we\'ll set up billing for you.');
      setPaymentBusy(false);
    }
  };

  const handleManageBilling = async () => {
    if (!organizationId) return;
    setPaymentBusy(true);
    setPaymentError(null);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-portal', {
        body: { organization_id: organizationId },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('No portal URL returned');
      window.location.href = data.url;
    } catch (err) {
      console.error('stripe-portal failed:', err);
      setPaymentError(err?.message || 'Could not open the Stripe billing portal.');
      setPaymentBusy(false);
    }
  };

  const formatBrand = (b) => {
    if (!b) return 'Card';
    return b.charAt(0).toUpperCase() + b.slice(1);
  };

  const handleCurrencyChange = (code) => {
    setCurrency(code);
    savePreferredCurrency(code);
    setCurrencyOpen(false);
  };

  const currentTier = useMemo(
    () => agreement
      ? { id: 'enterprise', name: agreement.plan_name, rateUsd: Number(agreement.rate_usd_per_hour) }
      : tierForLessonCount(usage.sessionsThisMonth),
    [usage.sessionsThisMonth, agreement]
  );
  const baseFeeUsd = agreement ? Number(agreement.base_fee_usd_per_month) : BASE_FEE_USD;

  const subtotalUsd = baseFeeUsd + usage.hoursThisMonth * currentTier.rateUsd;
  const estimatedUsd = billing.discountPercent ? subtotalUsd * (1 - billing.discountPercent / 100) : subtotalUsd;

  const applyDiscount = async (remove = false) => {
    if (!organizationId) return;
    setDiscountBusy(true);
    setDiscountError('');
    try {
      const { data, error } = await supabase.functions.invoke('billing-discount', {
        body: remove ? { organization_id: organizationId, remove: true } : { organization_id: organizationId, code: discountInput },
      });
      const res = data || {};
      if (error || !res.ok) {
        let msg = res.error;
        if (!msg && error?.context?.json) { try { msg = (await error.context.json()).error; } catch (_) { /* ignore */ } }
        setDiscountError(msg || 'Could not apply that code');
      } else {
        setDiscountInput('');
        await loadBilling();
      }
    } finally {
      setDiscountBusy(false);
    }
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className={`h-8 w-48 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className={`${cardClass} h-32`} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`${cardClass} h-64`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1
          className={`text-2xl font-semibold font-[Manrope] tracking-tight ${
            isDark ? 'text-white' : 'text-[#1a1a1a]'
          }`}
        >
          Billing
        </h1>
        <div className="relative">
          <button
            onClick={() => setCurrencyOpen((o) => !o)}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              isDark
                ? 'bg-[#121212] border-[#27272a] text-white hover:bg-[#18181b]'
                : 'bg-white border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]'
            }`}
          >
            <span>Currency: {currency}</span>
            <ChevronDown className="h-3 w-3" />
          </button>
          {currencyOpen && (
            <div
              className={`absolute right-0 mt-1 w-56 rounded-lg border shadow-lg z-10 max-h-72 overflow-y-auto ${
                isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'
              }`}
            >
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  onClick={() => handleCurrencyChange(c.code)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between transition-colors ${
                    c.code === currency
                      ? isDark
                        ? 'bg-[#18181b] text-white'
                        : 'bg-[#f5f5f5] text-[#1a1a1a]'
                      : isDark
                      ? 'text-[#a1a1aa] hover:bg-[#18181b]'
                      : 'text-[#666] hover:bg-[#f5f5f5]'
                  }`}
                >
                  <span>
                    <span className="font-medium">{c.code}</span>{' '}
                    <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>· {c.label}</span>
                  </span>
                  {c.code === currency && <Check className="h-3 w-3" />}
                </button>
              ))}
            </div>
          )}
          <p className={`text-[10px] mt-1 text-right ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            Rates updated: {RATES_UPDATED}
          </p>
        </div>
      </div>

      {/* This Month's Usage */}
      <div className={`${cardClass} p-5`}>
        <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
          This Month's Usage
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Sessions</span>
            <div
              className={`text-2xl font-semibold font-[Manrope] ${
                isDark ? 'text-white' : 'text-[#1a1a1a]'
              }`}
            >
              {usage.sessionsThisMonth}
            </div>
          </div>
          <div>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Hours Processed</span>
            <div
              className={`text-2xl font-semibold font-[Manrope] ${
                isDark ? 'text-white' : 'text-[#1a1a1a]'
              }`}
            >
              {usage.hoursThisMonth}h
            </div>
          </div>
          <div>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Current Tier</span>
            <div
              className={`text-2xl font-semibold font-[Manrope] ${
                isDark ? 'text-white' : 'text-[#1a1a1a]'
              }`}
            >
              {currentTier.name}
            </div>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              {formatCurrency(currentTier.rateUsd, currency, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}/hr
            </span>
          </div>
          <div>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Estimated this month
            </span>
            <div
              className={`text-2xl font-semibold font-[Manrope] ${
                isDark ? 'text-white' : 'text-[#1a1a1a]'
              }`}
            >
              {formatCurrency(estimatedUsd, currency)}
            </div>
            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              {formatCurrency(baseFeeUsd, currency)} base + {usage.hoursThisMonth}h ×{' '}
              {formatCurrency(currentTier.rateUsd, currency, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
              {billing.discountPercent ? ` − ${billing.discountPercent}% (${billing.discountCode})` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Base fee callout */}
      <div
        className={`${cardClass} p-4 flex items-center gap-3 ${
          isDark ? 'bg-[#18181b]' : 'bg-[#fafafa]'
        }`}
      >
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isDark ? 'bg-[#7662ac]/10' : 'bg-[#586f8d]/10'
          }`}
        >
          <Crown className={`w-4 h-4 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
        </div>
        <div>
          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
            {formatCurrency(baseFeeUsd, currency)} {currency !== 'USD' && `(≈ $${baseFeeUsd.toFixed(2)} USD)`} /month base fee
          </div>
          <div className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {agreement ? 'As set out in your enterprise agreement.' : 'Applies to all accounts, regardless of tier.'}
          </div>
        </div>
      </div>

      {/* Enterprise agreement replaces the public volume tiers */}
      {agreement && (
        <div className={`${cardClass} p-5`}>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{agreement.plan_name}</h3>
              <p className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                Effective {new Date(agreement.effective_from).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/30">Current plan</span>
          </div>
          <div className={`text-2xl font-semibold font-[Manrope] mb-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
            {formatCurrency(Number(agreement.rate_usd_per_hour), currency, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
            <span className={`text-sm font-normal ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>/hr</span>
          </div>
          <p className={`text-xs mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            Flat rate on all lesson hours, plus {formatCurrency(baseFeeUsd, currency)}/month platform fee.
          </p>
          {Array.isArray(agreement.terms) && agreement.terms.length > 0 && (
            <ul className={`space-y-1.5 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              {agreement.terms.map((t, i) => (
                <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-400" />{t}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Tier cards */}
      {!agreement && (
      <div>
        <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
          Volume Tiers
        </h3>
        <p className={`text-xs mb-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          Tier is auto-determined each month by total lesson count. All hours that month bill at the
          tier's flat rate.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((tier) => {
            const isCurrent = tier.id === currentTier.id;
            return (
              <div
                key={tier.id}
                className={`${cardClass} p-5 relative ${isCurrent ? tier.border + ' border-2' : ''}`}
              >
                {isCurrent && (
                  <div
                    className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-medium ${tier.bg} ${tier.color}`}
                  >
                    Current Tier
                  </div>
                )}
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${tier.bg}`}
                >
                  <tier.icon className={`w-5 h-5 ${tier.color}`} strokeWidth={1.5} />
                </div>
                <h4
                  className={`text-base font-semibold font-[Manrope] mb-1 ${
                    isDark ? 'text-white' : 'text-[#1a1a1a]'
                  }`}
                >
                  {tier.name}
                </h4>
                <p className={`text-xs mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  {tier.desc}
                </p>
                <div className="mb-4">
                  <span
                    className={`text-2xl font-semibold font-[Manrope] ${
                      isDark ? 'text-white' : 'text-[#1a1a1a]'
                    }`}
                  >
                    {formatCurrency(tier.rateUsd, currency)}
                    <span
                      className={`text-xs font-normal ${
                        isDark ? 'text-[#52525b]' : 'text-[#999]'
                      }`}
                    >
                      /hr
                    </span>
                  </span>
                </div>
                <div className={`space-y-2 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  <div className="flex items-center gap-2">
                    <Check className={`h-3 w-3 ${tier.color}`} />
                    <span>{tier.rangeLabel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`h-3 w-3 ${tier.color}`} />
                    <span>Flat rate on all hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`h-3 w-3 ${tier.color}`} />
                    <span>AI issue detection</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`h-3 w-3 ${tier.color}`} />
                    <span>Quality assessments</span>
                  </div>
                </div>
                {tier.id === 'scale' && (
                  <a
                    href="mailto:sales@tutortracer.com?subject=Enterprise%20volume%20pricing"
                    className={`block mt-4 text-xs font-medium ${tier.color} hover:underline`}
                  >
                    Above 10k lessons? Talk to us →
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Billing status banner — only when something needs the user's attention. */}
      {billing.status === 'past_due' && (
        <div
          className={`rounded-lg border p-4 flex items-start gap-3 ${
            isDark
              ? 'bg-red-500/10 border-red-500/30 text-red-300'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="text-xs">
            <div className="font-medium mb-0.5">Payment past due</div>
            Your most recent invoice failed to charge. Update your payment method via
            &ldquo;Manage billing&rdquo; below to avoid service interruption.
          </div>
        </div>
      )}
      {billing.status === 'canceled' && (
        <div
          className={`rounded-lg border p-4 flex items-start gap-3 ${
            isDark
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="text-xs">
            <div className="font-medium mb-0.5">Subscription canceled</div>
            Add a payment method to reactivate billing.
          </div>
        </div>
      )}

      {/* Payment Method */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            Payment Method
          </h3>
        </div>

        {billing.last4 ? (
          <div
            className={`flex items-center gap-3 p-3 rounded-lg ${
              isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'
            }`}
          >
            <CreditCard className={`h-5 w-5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                {formatBrand(billing.brand)} {'••••'} {billing.last4}
                {billing.exp ? (
                  <span className={`ml-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                    {'·'} exp {billing.exp}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`flex items-center gap-3 p-3 rounded-lg ${
              isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'
            }`}
          >
            <CreditCard className={`h-5 w-5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
            <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
              No payment method on file
            </span>
          </div>
        )}

        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#f0f0f0]'}`}>
          <div className={`text-xs font-medium mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Discount code</div>
          {billing.discountCode ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`px-2 py-0.5 rounded-md font-mono text-xs ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}>{billing.discountCode}</span>
              <span className={isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}>
                {billing.discountPercent ? `${billing.discountPercent}% off every monthly invoice` : 'Applied to every monthly invoice'}
              </span>
              <button onClick={() => applyDiscount(true)} disabled={discountBusy} className="text-xs underline underline-offset-2 opacity-70 hover:opacity-100 disabled:opacity-40">Remove</button>
            </div>
          ) : (
            <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); applyDiscount(false); }}>
              <input
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className={`h-9 px-3 rounded-lg border text-sm font-mono w-48 ${isDark ? 'bg-[#121212] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
              />
              <button type="submit" disabled={discountBusy || !discountInput.trim()} className={`h-9 px-4 text-xs font-medium rounded-lg border disabled:opacity-50 ${isDark ? 'border-[#27272a] text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}>
                {discountBusy ? 'Checking…' : 'Apply'}
              </button>
            </form>
          )}
          {discountError && <p className="mt-2 text-xs text-red-500">{discountError}</p>}
        </div>

        {billing.trialEndsAt && (
          <p className={`mt-3 text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {billing.trialEndsAt > new Date()
              ? `Your free trial runs until ${billing.trialEndsAt.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}. Lessons during the trial are free; adding a card now just keeps everything running when it ends.`
              : 'Your free trial has ended. Add a card to switch lesson imports back on. You are billed monthly for lessons after the trial, and lessons during the trial stay free.'}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {billing.last4 ? (
            <button
              onClick={handleManageBilling}
              disabled={paymentBusy}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                isDark
                  ? 'bg-white text-[#1a1a1a] hover:bg-[#e5e5e5]'
                  : 'bg-[#1a1a1a] text-white hover:bg-[#333]'
              }`}
            >
              {paymentBusy && <Loader2 className="h-3 w-3 animate-spin" />}
              Manage billing {'→'}
            </button>
          ) : (
            <button
              onClick={developerAccess ? handleAddPaymentMethod : undefined}
              disabled={paymentBusy || !developerAccess}
              title={developerAccess ? undefined : 'Payment methods are disabled on this demo account'}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                isDark
                  ? 'bg-white text-[#1a1a1a] hover:bg-[#e5e5e5]'
                  : 'bg-[#1a1a1a] text-white hover:bg-[#333]'
              }`}
            >
              {paymentBusy && <Loader2 className="h-3 w-3 animate-spin" />}
              Add payment method {'→'}
            </button>
          )}
          {billing.hasCustomer && !billing.last4 && (
            <button
              onClick={handleManageBilling}
              disabled={paymentBusy}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                isDark
                  ? 'bg-[#121212] border-[#27272a] text-white hover:bg-[#18181b]'
                  : 'bg-white border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]'
              }`}
            >
              View past invoices
            </button>
          )}
        </div>

        {paymentError && (
          <p className="text-xs mt-2 text-red-500">{paymentError}</p>
        )}
        <p className={`text-xs mt-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          Payments are processed securely by Stripe. Invoices for this month&apos;s usage are
          generated at the start of the following month.
        </p>
      </div>
    </div>
  );
};

export default Billing;
